import { useCartKeyContext } from '@/features/pos/components/Cart/contexts/CartKey'
import {
    getIsCartInFlightRequests,
    useCartOptimisticUpdateCounterActions,
} from '@/features/pos/components/Cart/hooks/cart-optimistic-update-counter'
import { updateCartContainerItemQuantityCache } from '@/features/pos/components/Cart/hooks/useChangeCartContainerItemQuantity/utils'
import { BatchCallback } from '@/features/pos/components/Cart/hooks/useChangeCartItemQuantity/types'
import { useBatch } from '@/features/pos/components/Cart/hooks/useChangeCartItemQuantity/useBatch'
import { cartContainerItemChangeQuantityMutation } from '@/features/pos/components/Cart/mutations'
import {
    getCartFromCache,
    writeCartContainerItemsToCache,
} from '@/features/pos/components/Cart/utils/cart-cache'
import {
    findCartContainerItemWithIndex,
    upsertCartContainerItem,
} from '@/features/pos/components/Cart/utils/cart-container-item'
import { logger } from '@/features/pos/logger'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useApolloClient, useMutation } from '@apollo/client'
import { useCallback } from 'react'
import invariant from 'tiny-invariant'

export const useChangeCartContainerItemQuantity = (): ((
    id: string,
    quantityChange: number
) => void) => {
    const handleError = useHandleErrorWithToast(logger)

    const [changeQuantity] = useMutation(cartContainerItemChangeQuantityMutation, {
        fetchPolicy: 'no-cache',
        onError: handleError,
    })

    const { cartKey } = useCartKeyContext()

    const client = useApolloClient()

    const { increment, lock, reset } = useCartOptimisticUpdateCounterActions()

    const batchCallback = useCallback<BatchCallback>(
        (id, pendingQuantity, onCompleted, onError) => {
            invariant(cartKey, "'cartKey' must be set")

            lock(id)

            const rollback = () => {
                reset(id)
                onError((quantityChange) =>
                    updateCartContainerItemQuantityCache(client, id, -quantityChange)
                )
            }

            changeQuantity({
                variables: {
                    cartKey,
                    changeQuantity: {
                        containerItemUuid: id,
                        quantityChange: pendingQuantity,
                    },
                },
                onCompleted: (data) => {
                    if (!data.cartContainerItemChangeQuantity) {
                        void handleError(
                            new Error('Cart container quantity mutation returned no cart')
                        )
                        rollback()
                        return
                    }

                    const responseItem = data.cartContainerItemChangeQuantity.containerItems.find(
                        ({ uuid }) => uuid === id
                    )

                    if (!responseItem) {
                        void handleError(
                            new Error('Cart container quantity mutation returned no changed item')
                        )
                        rollback()
                        return
                    }

                    onCompleted()

                    reset(id)

                    if (getIsCartInFlightRequests()) return

                    const currentCart = getCartFromCache(client.cache, cartKey)
                    const currentItem = findCartContainerItemWithIndex(
                        currentCart?.containerItems,
                        id
                    )

                    if (!currentItem) return

                    writeCartContainerItemsToCache(
                        client.cache,
                        cartKey,
                        upsertCartContainerItem(currentCart?.containerItems, responseItem)
                    )
                },
                onError: (error) => {
                    handleError(error)
                    rollback()
                },
            })
        },
        [cartKey, changeQuantity, client, handleError, lock, reset]
    )

    const handleExecuteEffect = useCallback(
        (id: string, quantityChange: number) => {
            increment(id)

            updateCartContainerItemQuantityCache(client, id, quantityChange)
        },
        [client, increment]
    )

    const skip = useCallback(
        (id: string, quantityChange: number) => {
            invariant(cartKey, "'cartKey' must be set")

            const currentCart = getCartFromCache(client.cache, cartKey)
            const foundItem = findCartContainerItemWithIndex(currentCart?.containerItems, id)

            return !foundItem || foundItem.item.quantity + quantityChange < 1
        },
        [client, cartKey]
    )

    const handleNoPendingQuantity = useCallback(
        (id: string) => {
            lock(id)
            reset(id)
        },
        [lock, reset]
    )

    return useBatch(batchCallback, handleExecuteEffect, skip, handleNoPendingQuantity)
}
